import { startGridMeasurement } from '@/lib/analytics/grid-navigation-performance'
import { features } from '@/lib/config'
import useGridNavigationPerformance from './use-grid-navigation-performance'
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  ASSETS_GRID_NAVIGATION_EVENT,
  beginGridNavigationTransition,
  getGridNavigationTransition,
  GRID_REVEAL_DURATION_MS,
  hasReachedGridDestination,
  revealGridNavigationTransition,
} from '@/lib/assets/grid-navigation-transition'

export type AssetsGridTransitionPhase = 'idle' | 'loading' | 'arriving' | 'revealing'
export function startAssetsGridTransition(href?: string) {
  startGridMeasurement(href)
  beginGridNavigationTransition(href)
}

export default function useAssetsGridTransition({
  isLoading,
  hasAssets,
  scope,
  enabled = true,
  contentVersion,
  queryIdentity,
  serverDataMs,
  serverPrepareMs,
}: {
  isLoading: boolean
  hasAssets: boolean
  scope: RefObject<HTMLElement | null>
  enabled?: boolean
  contentVersion?: unknown
  queryIdentity?: string
  serverDataMs?: number
  serverPrepareMs?: number
}) {
  useGridNavigationPerformance({ enabled, isLoading, hasAssets, scope, contentVersion, serverDataMs, serverPrepareMs })
  const transitionEnabled = enabled && features.compactCategoryNavigation
  const sawLoadingRef = useRef(false)
  const hasPresentedRef = useRef(false)
  const presentedQueryRef = useRef(queryIdentity)
  const [navigationId, setNavigationId] = useState(() => getGridNavigationTransition()?.id)
  const [phase, setPhase] = useState<AssetsGridTransitionPhase>(() =>
    transitionEnabled && getGridNavigationTransition()?.stage === 'loading' ? 'arriving' : 'idle'
  )

  useEffect(() => {
    if (!transitionEnabled) return
    const startTransition = () => {
      const active = getGridNavigationTransition()
      if (!active) {
        setNavigationId(undefined)
        setPhase('idle')
        return
      }
      if (!hasAssets) return
      setNavigationId(active.id)
      setPhase(active.stage === 'revealing' ? 'revealing' : 'loading')
    }
    window.addEventListener(ASSETS_GRID_NAVIGATION_EVENT, startTransition)
    return () => window.removeEventListener(ASSETS_GRID_NAVIGATION_EVENT, startTransition)
  }, [transitionEnabled, hasAssets])

  useEffect(() => {
    if (phase !== 'revealing') return
    const timeout = window.setTimeout(() => setPhase('idle'), GRID_REVEAL_DURATION_MS)
    return () => window.clearTimeout(timeout)
  }, [phase])

  useLayoutEffect(() => {
    if (!transitionEnabled) return
    if (isLoading) {
      sawLoadingRef.current = true
      const queryChanged = hasPresentedRef.current && presentedQueryRef.current !== queryIdentity
      if (hasAssets && (getGridNavigationTransition() || queryChanged)) {
        if (!getGridNavigationTransition()) beginGridNavigationTransition()
        setNavigationId(getGridNavigationTransition()?.id)
        setPhase(hasPresentedRef.current ? 'loading' : 'arriving')
      }
      return
    }

    hasPresentedRef.current = true
    presentedQueryRef.current = queryIdentity
    const active = getGridNavigationTransition()
    if (!active) {
      sawLoadingRef.current = false
      setPhase('idle')
      return
    }
    const destinationReady = active.destination ? hasReachedGridDestination() : sawLoadingRef.current
    if (active.stage !== 'loading' || !destinationReady) return
    if (!scope.current) return
    sawLoadingRef.current = false
    setPhase('arriving')
    let cancelled = false
    let frame = 0
    const deadline = performance.now() + 1000

    const waitForThumbnails = () => {
      if (cancelled || getGridNavigationTransition()?.id !== active.id) return
      const root = scope.current
      const cards = root ? Array.from(root.querySelectorAll<HTMLElement>('[data-grid-asset-key]')) : []
      const visibleCards = cards.filter((card) => {
        const bounds = card.getBoundingClientRect()
        return (
          bounds.width > 0 &&
          bounds.bottom > 0 &&
          bounds.top < window.innerHeight &&
          getComputedStyle(card).visibility !== 'hidden'
        )
      })
      const imagesReady = visibleCards.every((card) => {
        const image = card.querySelector<HTMLImageElement>('img')
        return !image || image.complete
      })
      if (hasAssets && (!visibleCards.length || !imagesReady) && performance.now() < deadline) {
        frame = requestAnimationFrame(waitForThumbnails)
        return
      }
      frame = requestAnimationFrame(() => {
        if (cancelled || !revealGridNavigationTransition(active.id)) return
        setPhase('revealing')
      })
    }
    frame = requestAnimationFrame(waitForThumbnails)
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [contentVersion, transitionEnabled, hasAssets, isLoading, navigationId, queryIdentity, scope])

  return phase
}
